import { cx } from './cx';
import { Text } from './Text';

export type ProgressTone = 'brand' | 'success' | 'warning' | 'error';

const fillClasses: Record<ProgressTone, string> = {
  brand: 'bg-brand-solid',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
};

export interface ProgressBarProps {
  /** 0–100. */
  value: number;
  tone?: ProgressTone;
  label?: string;
  /** Show the value as a trailing percentage. */
  showValue?: boolean;
  className?: string;
}

/**
 * Linear progress / score bar — a muted track with a tone-colored fill. Used
 * for DORA scorecards, coverage, rollout progress, etc.
 */
export const ProgressBar = ({
  value,
  tone = 'brand',
  label,
  showValue,
  className,
}: ProgressBarProps) => {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      {(label || showValue) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <Text variant="caption" tone="tertiary" className="font-medium">
              {label}
            </Text>
          )}
          {showValue && (
            <Text variant="caption" mono tone="secondary">
              {Math.round(pct)}%
            </Text>
          )}
        </div>
      )}
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-muted-strong"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className={cx(
            'h-full rounded-full transition-[width]',
            fillClasses[tone],
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
